import {
	Box,
	createStyles,
	Group,
	LoadingOverlay,
	PinInput,
	Stack,
	useMantineTheme,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useApiAuthStart, useApiAuthVerify } from '@repo/api-codegen';
import { trackAnonymousEvent } from '@repo/common/analytics';
import { Button, PlainButton, Text, Title } from '@repo/foundations';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { processAuthEvent } from '../../utils/crossTabAuth';
import { AuthCard } from './AuthCard';
import { AuthFooter } from './AuthLayouts/AuthFooter';
import { AuthLayoutBase } from './AuthLayouts/AuthLayoutBase';
import { anonymousId, referrer } from './AuthStepEmailComponent.constants';

const useStyles = createStyles((theme) => ({
	pinInputRoot: {
		gap: theme.spacing.xs,
	},
	pinInput: {
		fontSize: theme.fontSizes.xl,
		fontWeight: theme.other.typography.weight.bold,
		width: 45,
		height: 52,
		border: `solid 1px ${theme.other.getColor('border/input/default')}`,
		borderRadius: theme.radius.sm,
	},
}));

export interface AuthStepVerifyProps {
	initialCode?: string;
	initialVerifyErrorCode?: string;
	email?: string;
}

const CODE_LENGTH = 8;

export function AuthStepVerify({
	initialCode,
	initialVerifyErrorCode,
	email: propEmail,
}: AuthStepVerifyProps) {
	const styles = useStyles();
	const theme = useMantineTheme();
	const navigate = useNavigate();
	const [params] = useSearchParams();

	const email = propEmail || params.get('email');

	const [code, setCode] = useState<string>(initialCode || '');

	const [verifyCodeError, setVerifyCodeError] = useState<string | null>(
		initialVerifyErrorCode || null
	);

	// API hooks
	const { mutate: authStartMutation } = useApiAuthStart({
		onSuccess: (data) => {
			const { state, message } = data;

			// If state is not success, navigate to /auth
			if (state !== 'success') {
				navigate('/auth?error=' + encodeURIComponent(message || ''));
				return;
			}

			if (/^Can only resend in/.test(message || '')) {
				showNotification({
					message:
						'Verification codes can only be resent every few minutes. Please try again shortly.',
					color: 'red',
				});
			} else {
				showNotification({
					message: message || 'Verification code sent successfully',
					color: 'green',
				});
			}
		},
		onError: (error) => {
			showNotification({
				message: error?.detail || 'Failed to send verification code',
				color: 'red',
			});
		},
	});

	const {
		mutate: authVerifyMutation,
		error: authVerifyError,
		isPending: authVerifyPending,
	} = useApiAuthVerify({
		onSuccess: (data) => {
			const { state, message } = data;

			// If state is not success, handle error states
			if (state !== 'success') {
				let errorMessage = 'Verification failed';

				switch (state) {
					case 'invalid_email':
						errorMessage = 'Invalid email address';
						break;
					case 'no_verification_code':
						errorMessage =
							'No verification code found. Please request a new one.';
						break;
					case 'expired_code':
						errorMessage =
							'Verification code has expired. Please request a new one.';
						break;
					case 'too_many_attempts':
						errorMessage =
							'Too many verification attempts. Please try again later.';
						break;
					case 'invalid_code':
						errorMessage =
							'Invalid verification code. Please check and try again.';
						break;
					case 'sso_enforcement':
						errorMessage = 'SSO authentication required';
						break;
					default:
						errorMessage = message || 'Verification failed';
				}

				setVerifyCodeError(errorMessage);

				// For certain states, navigate to /auth
				if (state === 'sso_enforcement' || state === 'invalid_email') {
					setTimeout(() => {
						navigate('/auth?error=' + encodeURIComponent(errorMessage));
					}, 2000); // Give user time to read the error message
				}

				return;
			}

			// This is important as it will clear the query cache and reset the user state, in case a user was previously logged in. It will also sign out other tabs
			processAuthEvent({
				type: 'SIGN_IN',
				redirectPath: '/',
			});
		},
		onError: (error) => {
			setVerifyCodeError(error?.detail || 'Verification failed');
		},
	});

	// Get error from mutation or local state
	const displayError = verifyCodeError || authVerifyError?.detail || null;

	const handleResendCode = useCallback(() => {
		// If a user requests a new code
		setCode('');
		setVerifyCodeError(null);

		if (email) {
			authStartMutation({
				body: {
					email: email,
				},
			});
		}
	}, [authStartMutation, email]);

	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		if (email) {
			handleResendCode();
		}
	}, [handleResendCode, email]);

	const handleVerifyCode = useCallback(
		(verifyEmail: string, verifyCode: string) => {
			// We set an artificial delay to make the loading overlay visible.
			setTimeout(() => {
				trackAnonymousEvent(
					'auth/verify',
					{
						path: window.location.pathname,
						anonymous_id: anonymousId(),
						referrer: referrer(),
					},
					verifyEmail
				);

				authVerifyMutation({
					body: {
						email: verifyEmail,
						code: verifyCode,
					},
				});
			}, 300);
		},
		[authVerifyMutation]
	);

	const handleCodeChange = useCallback(
		(value: string) => {
			const cleanValue = value.toUpperCase().trim();
			setCode(cleanValue);
			if (cleanValue.length === CODE_LENGTH && cleanValue !== code && email) {
				handleVerifyCode(email, cleanValue);
			}
		},
		[handleVerifyCode, email, code]
	);

	return (
		<Box>
			<LoadingOverlay
				visible={authVerifyPending}
				overlayBlur={2}
				loaderProps={{ color: theme.other.neutralColors.black }}
			/>
			<AuthLayoutBase>
				<AuthCard p="2xl">
					<Stack spacing="3xs">
						<Title order={1}>Confirm it’s you</Title>
						<Text color="text/secondary/default">
							Enter the code sent to your email
						</Text>
					</Stack>
					<Stack spacing="md" my="xl">
						<PinInput
							classNames={{
								root: styles.classes.pinInputRoot,
								input: styles.classes.pinInput,
							}}
							error={displayError !== null}
							onChange={handleCodeChange}
							value={code}
							type="alphanumeric"
							inputMode="text"
							length={CODE_LENGTH}
							placeholder=""
							aria-label="One time code"
							autoFocus
						/>
						{displayError && (
							<Text size="sm" color="text/critical/default">
								{displayError}
							</Text>
						)}
					</Stack>
					<Group spacing="3xs">
						<Text size="sm" color="text/secondary/default">
							Didn’t receive a verification email?
						</Text>
						<PlainButton
							onClick={handleResendCode}
							size="sm"
							disabled={authVerifyPending}
						>
							Send another email
						</PlainButton>
					</Group>
					<AuthFooter>
						<Group spacing="xs" position="right" py="md" px="2xl">
							<Button onClick={() => navigate('/auth')} variant="default">
								Back
							</Button>
							<Button
								onClick={() => handleVerifyCode(email || '', code)}
								variant="primary"
								disabled={code.length !== CODE_LENGTH || authVerifyPending}
							>
								Continue
							</Button>
						</Group>
					</AuthFooter>
				</AuthCard>
			</AuthLayoutBase>
		</Box>
	);
}
